import { useEffect, useMemo, useState, useCallback } from 'react';
import { cn } from '@/lib/utils/cn';
import {
  FIRM_POLICY_CATEGORY_ORDER,
  type FirmNotificationPolicy,
  type NotificationAudience,
  type NotificationSeverity,
} from '@/lib/api/firm-notifications';

/**
 * Firm-wide notification policy editor (T-0112 / C20-122).
 *
 * Mirrors the prototype's `screens-firm.js → firmNotifs()` table — one
 * row per event_type, columns for In-app / Email / Daily digest toggles +
 * severity threshold + audience scopes. Per `handoff-inventory-protocol.md`
 * §1 the prototype is the visual contract; this component preserves the
 * row densities, category dividers, and toggle styling without
 * reinventing visual tokens (see `.toggle` styling in globals.css /
 * settings.css; we render the same shape with Tailwind primitives here).
 *
 * Behavioural notes:
 *   - The component is uncontrolled — it seeds local state from `policies`
 *     and surfaces a `dirty` boolean + `onSave` for the route to wire to a
 *     mutation. v1 has no backend (see `firm-notifications.ts` doc), so
 *     the save call is logged for review purposes.
 *   - Severity threshold uses an `<select>` with the four severity
 *     levels — the row dot uses the chosen severity color.
 *   - Audience scopes render as togglable scope-chips below the row
 *     label; clicking a chip flips inclusion.
 */

const AUDIENCE_LABELS: Record<NotificationAudience, string> = {
  firm_admin: 'Firm admin',
  engagement_lead: 'Engagement lead',
  consultant: 'Consultant',
};
const AUDIENCE_ORDER: readonly NotificationAudience[] = [
  'firm_admin',
  'engagement_lead',
  'consultant',
] as const;

const SEVERITY_LABELS: Record<NotificationSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  high: 'High',
  critical: 'Critical',
};
const SEVERITY_ORDER: readonly NotificationSeverity[] = [
  'info',
  'warning',
  'high',
  'critical',
] as const;

const SEVERITY_DOT_CLASS: Record<NotificationSeverity, string> = {
  info: 'bg-posture-healthy',
  warning: 'bg-posture-at-risk',
  high: 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
};

export interface NotificationPolicyEditorProps {
  policies: ReadonlyArray<FirmNotificationPolicy>;
  /** Save callback — receives the full edited policy list. */
  onSave: (next: FirmNotificationPolicy[]) => void;
  /** Whether the save mutation is in-flight. */
  saving?: boolean;
}

export function NotificationPolicyEditor({
  policies,
  onSave,
  saving,
}: NotificationPolicyEditorProps) {
  const [draft, setDraft] = useState<FirmNotificationPolicy[]>(() =>
    policies.map((p) => ({ ...p, audience_scopes: [...p.audience_scopes] })),
  );
  // Seed-or-replace when the upstream policies change (e.g. a successful
  // save invalidates the query and a new server snapshot arrives).
  useEffect(() => {
    setDraft(policies.map((p) => ({ ...p, audience_scopes: [...p.audience_scopes] })));
  }, [policies]);

  const dirty = useMemo(() => {
    if (draft.length !== policies.length) return true;
    for (let i = 0; i < draft.length; i += 1) {
      const a = draft[i];
      const b = policies[i];
      if (!a || !b) return true;
      if (
        a.severity !== b.severity ||
        a.in_app !== b.in_app ||
        a.email !== b.email ||
        a.digest !== b.digest ||
        a.audience_scopes.length !== b.audience_scopes.length
      ) {
        return true;
      }
      const aSet = new Set(a.audience_scopes);
      for (const s of b.audience_scopes) {
        if (!aSet.has(s)) return true;
      }
    }
    return false;
  }, [draft, policies]);

  const updateRow = useCallback((eventType: string, patch: Partial<FirmNotificationPolicy>) => {
    setDraft((prev) =>
      prev.map((row) => (row.event_type === eventType ? { ...row, ...patch } : row)),
    );
  }, []);

  const toggleAudience = useCallback((eventType: string, audience: NotificationAudience) => {
    setDraft((prev) =>
      prev.map((row) => {
        if (row.event_type !== eventType) return row;
        const has = row.audience_scopes.includes(audience);
        const next = has
          ? row.audience_scopes.filter((s) => s !== audience)
          : [...row.audience_scopes, audience];
        return { ...row, audience_scopes: next };
      }),
    );
  }, []);

  const handleReset = useCallback(() => {
    setDraft(policies.map((p) => ({ ...p, audience_scopes: [...p.audience_scopes] })));
  }, [policies]);

  const handleSave = useCallback(() => {
    onSave(draft);
  }, [onSave, draft]);

  // Group by category in the prototype's stable order.
  const grouped = useMemo(() => {
    const byCat = new Map<string, FirmNotificationPolicy[]>();
    for (const row of draft) {
      const list = byCat.get(row.category) ?? [];
      list.push(row);
      byCat.set(row.category, list);
    }
    return FIRM_POLICY_CATEGORY_ORDER.flatMap((cat) => {
      const list = byCat.get(cat);
      if (!list || list.length === 0) return [];
      return [{ category: cat, rows: list }];
    });
  }, [draft]);

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-ai-surface-border bg-ai-surface p-3 text-body-sm text-ai-ink">
        <strong className="font-semibold">Firm-wide defaults.</strong> Individual users may override
        per-event channel preferences from <span className="font-medium">My notifications</span>,
        and per-engagement preferences from each engagement's settings. Critical-severity rows
        cannot be fully muted by policy.
      </div>

      <div className="overflow-x-auto rounded-card border border-ink-100">
        <table className="w-full border-collapse text-body-sm">
          <thead>
            <tr className="border-b border-ink-100 bg-paper-50 text-left">
              <th
                scope="col"
                className="w-[42%] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Event
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                In-app
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Daily digest
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Severity
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Audience
              </th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ category, rows }) => (
              <PolicyCategoryRows
                key={category}
                category={category}
                rows={rows}
                onChannelChange={updateRow}
                onSeverityChange={(et, sev) => updateRow(et, { severity: sev })}
                onToggleAudience={toggleAudience}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-meta text-ink-500">
          {dirty
            ? 'Unsaved changes — press Save to publish to all firm members.'
            : 'No unsaved changes.'}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={!dirty || saving}
            className={cn(
              'inline-flex h-9 items-center gap-2 rounded-card border border-ink-100 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
              'hover:bg-paper-50 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className={cn(
              'inline-flex h-9 items-center gap-2 rounded-card bg-accent-amber-600 px-3 text-body-sm font-medium text-paper-0',
              'hover:bg-accent-amber-700 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface PolicyCategoryRowsProps {
  category: string;
  rows: FirmNotificationPolicy[];
  onChannelChange: (eventType: string, patch: Partial<FirmNotificationPolicy>) => void;
  onSeverityChange: (eventType: string, sev: NotificationSeverity) => void;
  onToggleAudience: (eventType: string, audience: NotificationAudience) => void;
}

function PolicyCategoryRows({
  category,
  rows,
  onChannelChange,
  onSeverityChange,
  onToggleAudience,
}: PolicyCategoryRowsProps) {
  return (
    <>
      <tr className="bg-paper-100">
        <td
          colSpan={6}
          className="px-3 py-1.5 text-meta font-semibold uppercase tracking-wider text-ink-700"
        >
          {category} · {rows.length} event{rows.length === 1 ? '' : 's'}
        </td>
      </tr>
      {rows.map((row) => (
        <PolicyRow
          key={row.event_type}
          row={row}
          onChannelChange={onChannelChange}
          onSeverityChange={onSeverityChange}
          onToggleAudience={onToggleAudience}
        />
      ))}
    </>
  );
}

interface PolicyRowProps {
  row: FirmNotificationPolicy;
  onChannelChange: (eventType: string, patch: Partial<FirmNotificationPolicy>) => void;
  onSeverityChange: (eventType: string, sev: NotificationSeverity) => void;
  onToggleAudience: (eventType: string, audience: NotificationAudience) => void;
}

function PolicyRow({ row, onChannelChange, onSeverityChange, onToggleAudience }: PolicyRowProps) {
  return (
    <tr className="border-t border-ink-100">
      <td className="px-3 py-2 align-top">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={cn('inline-block h-2 w-2 rounded-full', SEVERITY_DOT_CLASS[row.severity])}
          />
          <span className="font-medium text-ink-900">{row.label}</span>
        </div>
        <div className="mt-0.5 font-mono text-meta text-ink-500">{row.event_type}</div>
      </td>
      <td className="px-3 py-2 text-center align-middle">
        <ChannelToggle
          checked={row.in_app}
          onChange={(v) => onChannelChange(row.event_type, { in_app: v })}
          label={`In-app for ${row.label}`}
        />
      </td>
      <td className="px-3 py-2 text-center align-middle">
        <ChannelToggle
          checked={row.email}
          onChange={(v) => onChannelChange(row.event_type, { email: v })}
          label={`Email for ${row.label}`}
        />
      </td>
      <td className="px-3 py-2 text-center align-middle">
        <ChannelToggle
          checked={row.digest}
          onChange={(v) => onChannelChange(row.event_type, { digest: v })}
          label={`Digest for ${row.label}`}
        />
      </td>
      <td className="px-3 py-2 text-center align-middle">
        <select
          aria-label={`Severity for ${row.label}`}
          value={row.severity}
          onChange={(e) => onSeverityChange(row.event_type, e.target.value as NotificationSeverity)}
          className="h-7 rounded-card border border-ink-200 bg-paper-0 px-2 text-meta text-ink-700 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
        >
          {SEVERITY_ORDER.map((sev) => (
            <option key={sev} value={sev}>
              {SEVERITY_LABELS[sev]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex flex-wrap items-center gap-1.5">
          {AUDIENCE_ORDER.map((aud) => {
            const active = row.audience_scopes.includes(aud);
            return (
              <button
                key={aud}
                type="button"
                onClick={() => onToggleAudience(row.event_type, aud)}
                aria-pressed={active}
                className={cn(
                  'inline-flex h-6 items-center rounded-chip border px-2 text-meta font-medium transition-colors',
                  active
                    ? 'border-ink-300 bg-ink-100 text-ink-900'
                    : 'border-ink-100 bg-paper-0 text-ink-500 hover:bg-paper-50',
                )}
              >
                {AUDIENCE_LABELS[aud]}
              </button>
            );
          })}
        </div>
      </td>
    </tr>
  );
}

interface ChannelToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}

/**
 * Lightweight switch primitive — mirrors the prototype's `.toggle` CSS
 * rule (24×14px pill, ink-200 off / posture-healthy on). Built as a
 * native button so screen readers announce the role/state via aria-pressed.
 */
function ChannelToggle({ checked, onChange, label }: ChannelToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-4 w-7 flex-none items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
        checked ? 'bg-posture-healthy' : 'bg-ink-200',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inline-block h-3 w-3 rounded-full bg-paper-0 shadow-sm transition-transform',
          checked ? 'translate-x-3.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
